/**
 * The GM's combat dashboard.
 *
 * Composed by the model — which widgets, how many, their order — and rendered
 * from a closed set here. The GM only ever taps; typing during combat is what
 * makes it obvious to the table that software is involved.
 *
 * Two rules run through every widget below:
 *
 * 1. Nothing waits. Attack results are pre-rolled server-side, so a tap
 *    reveals a number that already exists. A spinner mid-fight reads as the
 *    GM stalling.
 * 2. Nothing is phrased as a suggestion. Text is speakable as-is, because the
 *    fiction is that this GM prepared a good encounter.
 */
import { useCallback, useEffect, useState } from 'react';

import type {
  Creature,
  CreatureAction,
  EncounterDesign,
  TerrainFeature,
  Widget,
} from '@/api/combat';

interface Props {
  design: EncounterDesign;
  prerolls: Record<string, number[]>;
  /** PC names, so the turn order includes the people at the table. */
  party?: string[];
  /** Server-computed. Shown so a GM can see the fight was actually checked
   *  rather than asserted — and spot when it wasn't. */
  scored: {
    difficulty: string | null;
    adjustedXp: number | null;
    threshold: number | null;
  };
  /** Put the board away without ending the fight — the GM is looking at
   *  something else for a moment. */
  onClose: () => void;
  /** The fight is over. Frees the session's single slot so the agent can
   *  prepare the next one. */
  onResolve: () => void;
  /** Opens the shared online battle map. Absent when the fight has no grid. */
  onOpenBattleMap?: () => void | Promise<void>;
}

export function CombatBoard({
  design,
  prerolls,
  party = [],
  scored,
  onClose,
  onResolve,
  onOpenBattleMap,
}: Props) {
  const [openingMap, setOpeningMap] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [round, setRound] = useState(1);
  const [clockFilled, setClockFilled] = useState(0);
  const [firedTriggers, setFiredTriggers] = useState<Set<number>>(new Set());

  const creaturesByName = new Map<string, Creature>(
    design.creatures.map((c) => [c.name, c]),
  );

  return (
    <div className="combat">
      {mapError ? (
        <p className="notice notice--error" role="alert">
          The shared board couldn’t open. Please try again.
        </p>
      ) : null}
      <header className="combat__bar">
        <div className="combat__id">
          <h2 className="combat__title">{design.title}</h2>
          <ScoreBadge scored={scored} target={design.target_difficulty} />
        </div>
        <div className="combat__round">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setRound((r) => Math.max(1, r - 1))}
            aria-label="Previous round"
          >
            −
          </button>
          <span className="combat__roundlabel">
            Round {round}
            <small> of ~{design.round_budget}</small>
          </span>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setRound((r) => r + 1)}
            aria-label="Next round"
          >
            +
          </button>
        </div>
        {onOpenBattleMap ? (
          <button
            type="button"
            className="button"
            disabled={openingMap}
            onClick={async () => {
              setOpeningMap(true);
              setMapError(false);
              try {
                await onOpenBattleMap();
              } catch {
                setMapError(true);
              } finally {
                setOpeningMap(false);
              }
            }}
            title="A shared grid your players open from a link"
          >
            {openingMap ? 'Opening map…' : 'Battle map'}
          </button>
        ) : null}
        <button
          type="button"
          className="button button--ghost"
          onClick={onClose}
        >
          Hide
        </button>
        <button type="button" className="button" onClick={onResolve}>
          Fight over
        </button>
      </header>

      <div className="combat__grid">
        {design.layout.map((w, i) => (
          <WidgetView
            key={`${w.type}-${i}`}
            widget={w}
            design={design}
            creaturesByName={creaturesByName}
            prerolls={prerolls}
            party={party}
            clockFilled={clockFilled}
            onAdvanceClock={() =>
              setClockFilled((n) => Math.min(design.clock.segments, n + 1))
            }
            fired={firedTriggers}
            onFire={(idx) =>
              setFiredTriggers((prev) => {
                const next = new Set(prev);
                if (next.has(idx)) next.delete(idx);
                else next.add(idx);
                return next;
              })
            }
            // A Call of Cthulhu playtest put "CR 3" on a cellar horror. CR is
            // a 5e scoring input and nothing else, so on a fight the server
            // could not score it is a number from a rulebook this table is not
            // using — displayed with the same confidence as a real one.
            showCr={scored.difficulty !== null}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The difficulty the server computed, not the one the model claimed.
 *
 * Shown because an unverified number is what this whole subsystem exists to
 * avoid: if scoring failed, the GM should see that rather than a confident
 * label over an unchecked fight.
 */
function ScoreBadge({
  scored,
  target,
}: {
  scored: Props['scored'];
  target: string;
}) {
  if (!scored.difficulty) {
    return <span className="badge badge--warn">Difficulty unverified</span>;
  }
  const onTarget = scored.difficulty === target;
  return (
    <span className={onTarget ? 'badge badge--ok' : 'badge badge--warn'}>
      {scored.difficulty}
      {scored.adjustedXp ? ` · ${scored.adjustedXp.toLocaleString()} XP` : ''}
      {!onTarget ? ` (asked for ${target})` : ''}
    </span>
  );
}

interface WidgetProps {
  widget: Widget;
  design: EncounterDesign;
  creaturesByName: Map<string, Creature>;
  prerolls: Record<string, number[]>;
  party: string[];
  clockFilled: number;
  onAdvanceClock: () => void;
  fired: Set<number>;
  onFire: (idx: number) => void;
  /** Challenge rating is a 5e scoring input. On an unscored fight it is a
   *  number from a rulebook this table is not using, so it stays off. */
  showCr: boolean;
}

function WidgetView(p: WidgetProps) {
  switch (p.widget.type) {
    case 'lines':
      return <LinesWidget widget={p.widget} design={p.design} />;
    case 'map':
      return <MapWidget design={p.design} />;
    case 'initiative':
      return (
        <InitiativeWidget
          design={p.design}
          prerolls={p.prerolls}
          party={p.party}
        />
      );
    case 'statblock':
      return (
        <StatblockWidget
          widget={p.widget}
          creatures={p.creaturesByName}
          showCr={p.showCr}
          prerolls={p.prerolls}
        />
      );
    case 'action':
      return (
        <ActionWidget
          widget={p.widget}
          creatures={p.creaturesByName}
          prerolls={p.prerolls}
        />
      );
    case 'clock':
      return (
        <ClockWidget
          design={p.design}
          filled={p.clockFilled}
          onAdvance={p.onAdvanceClock}
        />
      );
    case 'terrain':
      return <TerrainWidget design={p.design} />;
    case 'trigger':
      return (
        <TriggerWidget design={p.design} fired={p.fired} onFire={p.onFire} />
      );
    case 'tracker':
      return <TrackerWidget widget={p.widget} />;
    default:
      // A type the server allowed but this build has no component for. Absent
      // beats a blank card the GM taps at during a fight.
      return null;
  }
}

function Card({
  kind,
  title,
  children,
}: {
  kind: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`cw cw--${kind}`}>
      {title ? <h3 className="cw__title">{title}</h3> : null}
      {children}
    </section>
  );
}

function LinesWidget({
  widget,
  design,
}: {
  widget: Widget;
  design: EncounterDesign;
}) {
  const text =
    (widget.props?.text as string | undefined) ?? design.read_aloud.open ?? '';
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  }, [text]);

  if (!text) return null;
  return (
    <Card kind="lines" title={widget.label ?? 'Read aloud'}>
      <p className="cw__read">{text}</p>
      <button type="button" className="button button--ghost" onClick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </Card>
  );
}

/** Initiative as last left, so a mid-fight reload does not lose the turn order. */
function readStored(
  key: string,
): { rolled: Record<string, number>; order: boolean; turn: number } | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * The battle map, as something a GM can copy onto a mat in ten seconds.
 *
 * Asked for mid-fight: "combats should have maps on a grid where the enemies
 * are placed so i can draw it out for the players". The terrain list already
 * said what was in the room and never said where anything stood.
 *
 * SVG rather than a canvas or a table: it scales to a phone without going
 * fuzzy, prints, and every square is a real coordinate the GM can call out
 * ("the Spiral is at F4"). Column letters and row numbers are drawn for exactly
 * that reason — a map you cannot name a square on is a picture, not a tool.
 */
function MapWidget({ design }: { design: EncounterDesign }) {
  const map = design.map;
  if (!map || !map.width || !map.height) return null;

  const CELL = 26;
  const PAD = 18;
  const w = map.width * CELL;
  const h = map.height * CELL;

  const FILL: Record<string, string> = {
    wall: 'var(--ink-muted)',
    cover: 'var(--ink-faint)',
    difficult: 'var(--accent-wash)',
    hazard: 'var(--danger-wash)',
    water: 'var(--paper-sunken)',
    feature: 'var(--paper-sunken)',
  };
  const SIDE: Record<string, string> = {
    opposition: 'var(--danger)',
    ally: 'var(--ok)',
    party: 'var(--accent-strong)',
    neutral: 'var(--ink-muted)',
  };

  return (
    <Card kind="map" title="Map">
      <div className="map__scroll">
        <svg
          className="map__svg"
          viewBox={`0 0 ${w + PAD} ${h + PAD}`}
          role="img"
          aria-label={`Battle map, ${map.width} by ${map.height} squares of 5 feet`}
        >
          {/* Terrain first, so tokens sit on top of it. */}
          {map.features.map((f, i) => (
            <g key={`f${i}`}>
              <rect
                x={PAD + f.x * CELL}
                y={PAD + f.y * CELL}
                width={f.w * CELL}
                height={f.h * CELL}
                fill={FILL[f.kind] ?? 'var(--paper-sunken)'}
                opacity={f.kind === 'wall' ? 0.85 : 0.5}
              />
              <text
                x={PAD + f.x * CELL + 3}
                y={PAD + f.y * CELL + 11}
                className="map__flabel"
              >
                {f.name}
              </text>
            </g>
          ))}

          {/* Grid over the terrain: the GM counts squares on this. */}
          {Array.from({ length: map.width + 1 }, (_, i) => (
            <line
              key={`v${i}`}
              x1={PAD + i * CELL}
              y1={PAD}
              x2={PAD + i * CELL}
              y2={PAD + h}
              className="map__grid"
            />
          ))}
          {Array.from({ length: map.height + 1 }, (_, i) => (
            <line
              key={`hz${i}`}
              x1={PAD}
              y1={PAD + i * CELL}
              x2={PAD + w}
              y2={PAD + i * CELL}
              className="map__grid"
            />
          ))}

          {/* Coordinates, so a square can be named out loud. */}
          {Array.from({ length: map.width }, (_, i) => (
            <text
              key={`cx${i}`}
              x={PAD + i * CELL + CELL / 2}
              y={12}
              className="map__axis"
              textAnchor="middle"
            >
              {String.fromCharCode(65 + i)}
            </text>
          ))}
          {Array.from({ length: map.height }, (_, i) => (
            <text
              key={`cy${i}`}
              x={10}
              y={PAD + i * CELL + CELL / 2 + 4}
              className="map__axis"
              textAnchor="middle"
            >
              {i + 1}
            </text>
          ))}

          {map.placements.map((pl, i) => (
            <g key={`p${i}`}>
              <circle
                cx={PAD + pl.x * CELL + CELL / 2}
                cy={PAD + pl.y * CELL + CELL / 2}
                r={CELL / 2 - 3}
                fill={SIDE[pl.side] ?? 'var(--ink-muted)'}
              />
              <text
                x={PAD + pl.x * CELL + CELL / 2}
                y={PAD + pl.y * CELL + CELL / 2 + 4}
                className="map__token"
                textAnchor="middle"
              >
                {initials(pl.name)}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <ul className="map__key">
        {map.placements.map((pl, i) => (
          <li key={`k${i}`}>
            <span className="map__dot" style={{ background: SIDE[pl.side] }} />
            <strong>{initials(pl.name)}</strong> {pl.name}
            <span className="ref__dim">
              {' '}
              {String.fromCharCode(65 + pl.x)}
              {pl.y + 1}
            </span>
          </li>
        ))}
      </ul>
      {map.notes ? <p className="cw__note">{map.notes}</p> : null}
      <p className="ref__dim">Each square is 5 ft.</p>
    </Card>
  );
}

/** Two letters is what fits in a 26px token and what a GM can say out loud. */
function initials(name: string): string {
  const words = name
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * The turn order, for everyone in the room.
 *
 * A GM asked for "initiatives for everyone" and meant everyone: this used to
 * list the monsters and stop, which is the half of the turn order they already
 * had. The PCs are here too, in the same list, sorted together — a separate
 * list of players would just be a second thing to read.
 *
 * NPC rolls come off the server's seeded preroll queue for the same reason
 * attacks do: the number is already there when the GM taps, and a reload does
 * not silently change what the dice said. PCs roll at the table, so their rows
 * take a typed number — the one place on this board a GM types, because there
 * is no other way to get a number that was rolled in someone's hand.
 */
function InitiativeWidget({
  design,
  prerolls,
  party,
}: {
  design: EncounterDesign;
  prerolls: Record<string, number[]>;
  party: string[];
}) {
  const npcs = design.creatures.flatMap((c) =>
    Array.from({ length: c.count }, (_, i) => ({
      key: `${c.name}-${i}`,
      name: c.count > 1 ? `${c.name} ${i + 1}` : c.name,
      ac: c.ac,
      hp: c.hp,
      side: c.side ?? 'opposition',
    })),
  );

  // Kept across reloads, because a reload used to cost the GM the turn order
  // in the middle of a fight. The rolls themselves are seeded server-side and
  // never change, but which ones had been taken, and whose turn it was, lived
  // only in React state — so a refresh mid-combat lost all of it and there was
  // no way to get it back except by asking someone to read it out of the
  // database. Keyed by encounter so two fights cannot inherit each other.
  const key = `throughline.initiative.${design.title || 'encounter'}`;
  const [rolled, setRolled] = useState<Record<string, number>>(
    () => readStored(key)?.rolled ?? {},
  );
  const [order, setOrder] = useState(() => readStored(key)?.order ?? false);
  const [turn, setTurn] = useState(() => readStored(key)?.turn ?? 0);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify({ rolled, order, turn }));
    } catch {
      /* private browsing; the fight still runs, it just forgets on reload */
    }
  }, [key, rolled, order, turn]);

  const rollNpcs = () => {
    const next: Record<string, number> = { ...rolled };
    for (const n of npcs) {
      const q = prerolls[`initiative::${n.name}`] ?? [];
      // No queue means an older encounter built before initiative was rolled
      // server-side. Leave it blank rather than inventing a number here — a
      // roll the server cannot reproduce is worse than no roll.
      if (q.length > 0) next[n.key] = q[0]!;
    }
    setRolled(next);
    setOrder(true);
  };

  const rows = [
    ...npcs.map((n) => ({ ...n, pc: false })),
    ...party.map((name) => ({
      key: `pc-${name}`,
      name,
      ac: null,
      hp: null,
      side: 'pc',
      pc: true,
    })),
  ];
  const sorted = order
    ? [...rows].sort((a, b) => (rolled[b.key] ?? -1) - (rolled[a.key] ?? -1))
    : rows;

  return (
    <Card kind="initiative" title="Initiative">
      <div className="init__bar">
        <button
          type="button"
          className="button button--primary"
          onClick={rollNpcs}
        >
          Roll initiative
        </button>
        {order ? (
          <>
            <button
              type="button"
              className="button"
              onClick={() =>
                setTurn((t) => (t + 1) % Math.max(sorted.length, 1))
              }
            >
              Next turn
            </button>
            <span className="init__whose">{sorted[turn]?.name ?? ''}</span>
          </>
        ) : null}
      </div>
      <ul className="init">
        {sorted.map((r, i) => (
          <InitiativeRow
            key={r.key}
            name={r.name}
            ac={r.ac}
            hp={r.hp}
            pc={r.pc}
            ally={r.side === 'ally'}
            active={order && i === turn}
            init={rolled[r.key] ?? null}
            onInit={(v) => setRolled((p) => ({ ...p, [r.key]: v }))}
          />
        ))}
      </ul>
    </Card>
  );
}

function InitiativeRow({
  name,
  ac,
  hp,
  pc = false,
  ally = false,
  active = false,
  init = null,
  onInit,
}: {
  name: string;
  ac: number | null;
  hp: number | null;
  pc?: boolean;
  ally?: boolean;
  active?: boolean;
  init?: number | null;
  onInit?: (value: number) => void;
}) {
  const [current, setCurrent] = useState(hp ?? 0);
  const [step, setStep] = useState(5);
  const dead = hp != null && current <= 0;

  const initCell = (
    <input
      className="init__init"
      inputMode="numeric"
      aria-label={`Initiative for ${name}`}
      value={init ?? ''}
      placeholder="–"
      onChange={(e) => {
        const n = Number(e.target.value.replace(/[^0-9-]/g, ''));
        if (!Number.isNaN(n)) onInit?.(n);
      }}
    />
  );

  // A PC has no AC or HP here — the player has their own sheet, and duplicating
  // it on the GM's board is a second number to keep in sync and get wrong.
  if (pc) {
    return (
      <li className={active ? 'init__row init__row--active' : 'init__row'}>
        {initCell}
        <span className="init__name">
          {name} <span className="init__tag">PC</span>
        </span>
      </li>
    );
  }

  // A phase-one build can arrive without HP. The row is still worth having —
  // the GM needs the name in the initiative order — but a damage stepper
  // counting down from an unknown maximum tells them nothing, so it goes away
  // until the full build fills the number in.
  if (hp == null) {
    return (
      <li className={active ? 'init__row init__row--active' : 'init__row'}>
        {initCell}
        <span className="init__name">
          {name}
          {ally ? <span className="init__tag">ally</span> : null}
        </span>
        {ac != null ? <span className="init__ac">AC {ac}</span> : null}
        <span className="init__hp">HP —</span>
      </li>
    );
  }

  return (
    <li
      className={
        (dead ? 'init__row init__row--down' : 'init__row') +
        (active ? ' init__row--active' : '')
      }
    >
      {initCell}
      <span className="init__name">
        {name}
        {ally ? <span className="init__tag">ally</span> : null}
      </span>
      {ac != null ? <span className="init__ac">AC {ac}</span> : null}
      <span className="init__hp">
        {current}
        <small>/{hp}</small>
      </span>
      <span className="init__ctl">
        <button
          type="button"
          onClick={() => setCurrent((v) => v - step)}
          aria-label={`Damage ${name}`}
        >
          −{step}
        </button>
        <button
          type="button"
          onClick={() => setCurrent((v) => Math.min(hp, v + step))}
          aria-label={`Heal ${name}`}
        >
          +{step}
        </button>
        <button
          type="button"
          className="init__step"
          onClick={() => setStep((s) => (s === 5 ? 1 : s === 1 ? 10 : 5))}
          aria-label="Change damage step"
        >
          ±{step}
        </button>
      </span>
    </li>
  );
}

function StatblockWidget({
  widget,
  creatures,
  showCr,
  prerolls,
}: {
  widget: Widget;
  creatures: Map<string, Creature>;
  showCr: boolean;
  prerolls: Record<string, number[]>;
}) {
  const c = widget.ref ? creatures.get(widget.ref) : undefined;
  if (!c) return null;
  // Every number here is optional now. A phase-one build can arrive with a gap
  // in it — that is worth showing, and worth showing as a gap rather than as
  // the word "null" where a number goes.
  return (
    <Card
      kind="statblock"
      title={c.count > 1 ? `${c.name} ×${c.count}` : c.name}
    >
      <div className="sb__nums">
        {c.ac != null ? <span>AC {c.ac}</span> : null}
        {c.hp != null ? <span>HP {c.hp}</span> : null}
        {c.speed ? <span>{c.speed}</span> : null}
        {showCr && c.challenge_rating ? (
          <span className="sb__cr">CR {c.challenge_rating}</span>
        ) : null}
      </div>
      <p className="sb__sig">{c.signature}</p>
      {/* Every action, tappable, on the creature's own card.
          This card used to stop at the signature line: AC, HP and a sentence of
          flavour, and nothing about what the thing could actually do. The only
          rollable attacks were the separate `action` widgets, and the model
          emits a couple of those for a fight with seven creatures — so a GM
          mid-combat had numbers for two attacks out of eleven and said, fairly,
          "I need dice and damage, I don't know what actions these guys can
          take". The data was in the design the whole time. */}
      {c.actions?.length ? (
        <div className="sb__acts">
          {c.actions.map((a) => (
            <ActionRoll
              key={a.name}
              combatant={c.name}
              action={a}
              prerolls={prerolls}
            />
          ))}
        </div>
      ) : null}
    </Card>
  );
}

/**
 * One attack: what it is, what it needs, and the die already rolled for it.
 *
 * Shares the server's seeded queue with the standalone action widgets — same
 * key, so tapping the same attack in either place draws from one sequence and
 * a reload cannot change what the dice said.
 */
function ActionRoll({
  combatant,
  action,
  prerolls,
}: {
  combatant: string;
  action: CreatureAction;
  prerolls: Record<string, number[]>;
}) {
  const queue = prerolls[`${combatant}::${action.name}`] ?? [];
  const [idx, setIdx] = useState(-1);
  const roll =
    idx >= 0 && queue.length ? (queue[idx % queue.length] ?? null) : null;
  const total =
    roll !== null && action.to_hit != null ? roll + action.to_hit : null;
  const crit = roll === 20;
  const fumble = roll === 1;

  // An action with no attack roll — a save-or-suck, or a bonus action that
  // grants something. There is nothing to tap, so it reads rather than rolls.
  const rollable = action.to_hit != null && queue.length > 0;

  return (
    <div className="sb__act">
      <button
        type="button"
        className="act act--inline"
        onClick={() => rollable && setIdx((i) => i + 1)}
        disabled={!rollable}
        aria-label={rollable ? `Roll ${action.name}` : action.name}
      >
        <span className="act__name">
          {action.name}
          {action.recharge ? <small> · {action.recharge}</small> : null}
        </span>
        <span className="act__meta">
          {action.to_hit != null ? `+${action.to_hit} to hit` : ''}
          {action.damage
            ? `${action.to_hit != null ? ' · ' : ''}${action.damage}`
            : ''}
          {action.save
            ? `${action.damage || action.to_hit != null ? ' · ' : ''}${action.save}`
            : ''}
        </span>
        {rollable ? (
          roll === null ? (
            <span className="act__hint">Tap</span>
          ) : (
            <span
              className={
                crit
                  ? 'act__res act__res--crit'
                  : fumble
                    ? 'act__res act__res--fumble'
                    : 'act__res'
              }
            >
              {total !== null ? total : roll}
              <small>{crit ? 'crit' : fumble ? 'fumble' : `d20 ${roll}`}</small>
            </span>
          )
        ) : null}
      </button>
      {action.notes ? <p className="cw__note">{action.notes}</p> : null}
    </div>
  );
}

/**
 * A tappable attack. The result is already decided — this reveals the next
 * value off a server-seeded queue rather than rolling anything now.
 */
function ActionWidget({
  widget,
  creatures,
  prerolls,
}: {
  widget: Widget;
  creatures: Map<string, Creature>;
  prerolls: Record<string, number[]>;
}) {
  const props = widget.props ?? {};
  const combatant = (props.combatant as string | undefined) ?? widget.ref;
  const creature = combatant ? creatures.get(combatant) : undefined;

  // The widget's own props are the source of truth, not a lookup into the
  // creature. The model composes this display deliberately — it decides which
  // of a creature's actions get their own button and how each is labelled.
  //
  // Reading `label` and falling back to `actions[0]` (which is what this did
  // first) silently collapsed every action for a creature onto its first one:
  // the Bellwarden's three buttons all rendered as "Heavy Mace". The name
  // arrives as `props.name`.
  const name =
    (props.name as string | undefined) ??
    (props.label as string | undefined) ??
    widget.label ??
    '';
  const fromCreature = creature?.actions.find((a) => a.name === name);
  const action: CreatureAction | undefined = name
    ? {
        name,
        to_hit: (props.to_hit as number | undefined) ?? fromCreature?.to_hit,
        damage: (props.damage as string | undefined) ?? fromCreature?.damage,
        save: (props.save as string | undefined) ?? fromCreature?.save,
        recharge:
          (props.recharge as string | undefined) ?? fromCreature?.recharge,
        notes: (props.notes as string | undefined) ?? fromCreature?.notes,
      }
    : fromCreature;

  // Must match the server's preroll key: "<creature>::<action>".
  const key = `${combatant ?? '?'}::${action?.name ?? name}`;
  const queue = prerolls[key] ?? [];
  const [idx, setIdx] = useState(-1);

  if (!action) return null;
  // `?? null` rather than a non-null assertion: an empty preroll queue is a
  // real state (an action the server found no dice for), and it should show
  // the untapped face instead of NaN in front of a GM mid-fight.
  const roll =
    idx >= 0 && queue.length ? (queue[idx % queue.length] ?? null) : null;
  const total =
    roll !== null && action.to_hit != null ? roll + action.to_hit : null;
  const crit = roll === 20;
  const fumble = roll === 1;

  return (
    <Card kind="action">
      <button
        type="button"
        className="act"
        onClick={() => setIdx((i) => i + 1)}
        aria-label={`Roll ${action.name}`}
      >
        <span className="act__name">
          {action.name}
          {action.recharge ? <small> · {action.recharge}</small> : null}
        </span>
        <span className="act__meta">
          {action.to_hit != null
            ? `+${action.to_hit} to hit`
            : (action.save ?? '')}
          {action.damage ? ` · ${action.damage}` : ''}
        </span>
        {roll === null ? (
          <span className="act__hint">Tap</span>
        ) : (
          <span
            className={
              crit
                ? 'act__res act__res--crit'
                : fumble
                  ? 'act__res act__res--fumble'
                  : 'act__res'
            }
          >
            {total !== null ? total : roll}
            <small>
              {crit
                ? 'critical'
                : fumble
                  ? 'fumble'
                  : total !== null
                    ? `d20 ${roll}`
                    : ''}
            </small>
          </span>
        )}
      </button>
      {action.notes ? <p className="cw__note">{action.notes}</p> : null}
      {idx >= 0 ? (
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setIdx((i) => i + 1)}
        >
          Reroll
        </button>
      ) : null}
    </Card>
  );
}

function ClockWidget({
  design,
  filled,
  onAdvance,
}: {
  design: EncounterDesign;
  filled: number;
  onAdvance: () => void;
}) {
  const { label, segments, on_expire, advances_when } = design.clock;
  const done = filled >= segments;
  return (
    <Card kind="clock" title={label}>
      <div
        className="clock__segs"
        role="img"
        aria-label={`${filled} of ${segments} filled`}
      >
        {Array.from({ length: segments }, (_, i) => (
          <span
            key={i}
            className={i < filled ? 'clock__seg clock__seg--on' : 'clock__seg'}
          />
        ))}
      </div>
      <button
        type="button"
        className="button button--primary"
        onClick={onAdvance}
        disabled={done}
      >
        {done ? 'Expired' : 'Advance'}
      </button>
      <p className={done ? 'cw__note cw__note--hot' : 'cw__note'}>
        {on_expire}
      </p>
      {advances_when?.length ? (
        <ul className="cw__list">
          {advances_when.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function TerrainWidget({ design }: { design: EncounterDesign }) {
  const features: TerrainFeature[] = design.terrain ?? [];
  if (!features.length) return null;
  return (
    <Card kind="terrain" title="Terrain">
      <ul className="terrain">
        {features.map((f) => (
          <li key={f.name}>
            <strong>{f.name}</strong>
            <span>{f.effect}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function TriggerWidget({
  design,
  fired,
  onFire,
}: {
  design: EncounterDesign;
  fired: Set<number>;
  onFire: (i: number) => void;
}) {
  return (
    <Card kind="trigger" title="Escalation">
      <ul className="trig">
        {design.escalation.map((e, i) => (
          <li
            key={e.when}
            className={
              fired.has(i) ? 'trig__row trig__row--fired' : 'trig__row'
            }
          >
            <button type="button" onClick={() => onFire(i)}>
              <span className="trig__when">{e.when}</span>
              <span className="trig__then">{e.then}</span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function TrackerWidget({ widget }: { widget: Widget }) {
  const items = (widget.props?.items as string[] | undefined) ?? [];
  const [checked, setChecked] = useState<Set<string>>(new Set());
  if (!items.length) return null;
  return (
    <Card kind="tracker" title={widget.label ?? 'Track'}>
      <ul className="track">
        {items.map((it) => (
          <li key={it}>
            <button
              type="button"
              className={checked.has(it) ? 'track__on' : undefined}
              onClick={() =>
                setChecked((prev) => {
                  const next = new Set(prev);
                  if (next.has(it)) next.delete(it);
                  else next.add(it);
                  return next;
                })
              }
            >
              {it}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
